export interface ParsedUsAddress {
  line1: string;
  line2?: string;
  city: string;
  /** Always the 2-letter state/territory code. */
  state: string;
  postalCode: string;
}

// Matches "Line1, City, State Zip" and "Line1, Line2, City, State Zip".
// State may be a 2-letter code ("NC") or a full state name ("North Carolina").
const US_ADDRESS_REGEX =
  /^([^,]+),\s*(?:([^,]+),\s*)?([^,]+),\s*([A-Za-z][A-Za-z.\s]*?)\s+(\d{5}(?:-\d{4})?)$/;

const US_STATE_CODES = new Set([
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "DC", "FL", "GA", "HI", "ID",
  "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD", "MA", "MI", "MN", "MS", "MO",
  "MT", "NE", "NV", "NH", "NJ", "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA",
  "RI", "SC", "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY",
  "AS", "GU", "MP", "PR", "VI",
]);

const US_STATE_NAME_TO_CODE: Record<string, string> = {
  alabama: "AL", alaska: "AK", arizona: "AZ", arkansas: "AR",
  california: "CA", colorado: "CO", connecticut: "CT", delaware: "DE",
  "district of columbia": "DC", florida: "FL", georgia: "GA", hawaii: "HI",
  idaho: "ID", illinois: "IL", indiana: "IN", iowa: "IA", kansas: "KS",
  kentucky: "KY", louisiana: "LA", maine: "ME", maryland: "MD",
  massachusetts: "MA", michigan: "MI", minnesota: "MN", mississippi: "MS",
  missouri: "MO", montana: "MT", nebraska: "NE", nevada: "NV",
  "new hampshire": "NH", "new jersey": "NJ", "new mexico": "NM",
  "new york": "NY", "north carolina": "NC", "north dakota": "ND", ohio: "OH",
  oklahoma: "OK", oregon: "OR", pennsylvania: "PA", "rhode island": "RI",
  "south carolina": "SC", "south dakota": "SD", tennessee: "TN", texas: "TX",
  utah: "UT", vermont: "VT", virginia: "VA", washington: "WA",
  "west virginia": "WV", wisconsin: "WI", wyoming: "WY",
  "american samoa": "AS", guam: "GU", "northern mariana islands": "MP",
  "puerto rico": "PR", "virgin islands": "VI",
};

/**
 * Normalizes a state to its 2-letter code, accepting either a code ("nc") or a
 * full state name ("North Carolina"). Returns null for anything unrecognized,
 * which is how non-US addresses get rejected.
 */
export function toUsStateCode(state: string): string | null {
  const normalized = state.replace(/\./g, "").trim().replace(/\s+/g, " ");
  const upper = normalized.toUpperCase();
  if (US_STATE_CODES.has(upper)) return upper;
  return US_STATE_NAME_TO_CODE[normalized.toLowerCase()] ?? null;
}

/**
 * Parses a single-line US address into its parts. Returns null when the line
 * does not match a supported US format, in which case callers should omit the
 * address rather than show/send a partial one.
 *
 * Keep in sync with the backend's `@domain/helpers/usAddress`, so the Create
 * Disbursement preview shows exactly the address the API request will carry.
 */
export function parseUsAddress(donorAddress: string): ParsedUsAddress | null {
  const match = US_ADDRESS_REGEX.exec(donorAddress.trim());
  if (!match) return null;

  const [, line1, line2, city, state, postalCode] = match;
  const stateCode = toUsStateCode(state);
  if (!stateCode) return null;

  return {
    line1: line1.trim(),
    line2: line2 ? line2.trim() : undefined,
    city: city.trim(),
    state: stateCode,
    postalCode: postalCode.trim(),
  };
}
